import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Download, ExternalLink, Eye, FileText, Plus, X } from "lucide-react";
import clsx from "clsx";
import {
  Contracts,
  type ContractDetail,
  type ContractKind,
  type ContractVersion,
} from "@/api/contracts";
import { Accounts, Config, Deals } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { fileSize } from "@/api/demos";
import { toast } from "@/lib/feedback";
import { KINDS, daysUntil, inDays } from "@/lib/contracts";
import { fmtDay, fmtDayShort } from "@/lib/format";
import { Modal } from "@/components/demos/DemoDialogs";
import { useContractSave } from "@/lib/useContractSave";

function useVersionUrl(contractId: string, versionId: string | null) {
  return useQuery({
    queryKey: ["contract-link", versionId],
    queryFn: () => Contracts.versionLink(contractId, versionId!),
    enabled: !!versionId,
    staleTime: 20 * 60_000, // links last 30 minutes
  });
}

/** The document, rendered in place when the browser can (PDF, images, text). */
export function VersionViewer({
  contractId,
  version: v,
  onClose,
}: {
  contractId: string;
  version: ContractVersion;
  onClose: () => void;
}) {
  const url = useVersionUrl(contractId, v.id).data;
  const ct = v.content_type.toLowerCase();
  const inline = ct === "application/pdf" || ct.startsWith("text/");
  return (
    <Modal title={`Version ${v.version} · ${v.filename}`} onClose={onClose} wide>
      <div className="space-y-3">
        {!url ? (
          <div className="grid h-[60vh] place-items-center text-mut-500">Loading…</div>
        ) : inline ? (
          <iframe
            title={v.filename}
            src={url}
            className="h-[70dvh] w-full rounded-xl border border-fg/[0.08] bg-white"
          />
        ) : ct.startsWith("image/") ? (
          <img src={url} alt={v.filename} className="mx-auto max-h-[70dvh] rounded-xl" />
        ) : (
          <div className="grid h-48 place-items-center rounded-xl border border-dashed border-fg/[0.15] px-6 text-center text-md text-mut-500">
            This file type can't be previewed in the browser. Download it to open it in Word or your
            PDF reader.
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-mut-500">
          <span>{fileSize(v.size_bytes)}</span>
          <span>
            {v.uploaded_by_name ?? "Someone"} · {fmtDay(v.created_at)}
          </span>
          <span className="flex-1" />
          {url && (
            <>
              <a className="btn btn-ghost !py-1" href={url} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5" /> Open in a tab
              </a>
              <a className="btn btn-ghost !py-1" href={`${url}&download=1`}>
                <Download className="h-3.5 w-3.5" /> Download
              </a>
            </>
          )}
        </div>
        {v.note && <p className="text-md text-mut-200">{v.note}</p>}
      </div>
    </Modal>
  );
}

/** Current version up top, history below. */
export function DocumentPanel({
  contract: c,
  onView,
}: {
  contract: ContractDetail;
  onView: (versionId: string) => void;
}) {
  const [latest, ...older] = c.versions;
  return (
    <section className="space-y-3">
      <h3 className="section-title">Document</h3>
      {!latest ? (
        <p className="text-sm text-mut-500">
          No document yet. Attach a draft with the paperclip in the thread.
        </p>
      ) : (
        <>
          <button
            onClick={() => onView(latest.id)}
            className="flex w-full items-center gap-3 rounded-2xl border border-fg/[0.08] bg-ink-900 px-3 py-3 text-left hover:border-fg/[0.16]"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent-azure/15 text-accent-azure">
              <FileText className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-md font-semibold">{latest.filename}</span>
              <span className="block text-sm text-mut-500">
                Version {latest.version} · {fmtDayShort(latest.created_at)}
              </span>
            </span>
            <Eye className="h-4 w-4 text-mut-500" />
          </button>
          {older.length > 0 && (
            <ul className="space-y-0.5">
              {older.map((v) => (
                <li key={v.id}>
                  <button
                    onClick={() => onView(v.id)}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-fg/[0.04]"
                  >
                    <span className="w-7 shrink-0 font-medium text-mut-500">v{v.version}</span>
                    <span className="min-w-0 flex-1 truncate">{v.filename}</span>
                    <span className="shrink-0 text-xs text-mut-500">
                      {fmtDayShort(v.created_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

/** A field that saves itself when you leave it. Remounts when the saved value changes. */
function Field({
  label,
  value,
  type = "text",
  placeholder,
  onSave,
}: {
  label: string;
  value: string | number | null;
  type?: "text" | "date" | "number";
  placeholder?: string;
  onSave: (v: string) => void;
}) {
  const id = `f-${label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        key={String(value ?? "")}
        id={id}
        className="input"
        type={type}
        inputMode={type === "number" ? "decimal" : undefined}
        placeholder={placeholder}
        defaultValue={value ?? ""}
        onBlur={(e) => {
          if (e.target.value !== String(value ?? "")) onSave(e.target.value);
        }}
      />
    </div>
  );
}

/** Term dates, the notice deadline, and a checklist of obligations. */
export function KeyDatesPanel({ contract: c }: { contract: ContractDetail }) {
  const qc = useQueryClient();
  const save = useContractSave(c.id);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const noticeIn = c.notice_by ? daysUntil(c.notice_by) : null;

  const apply = (p: Promise<ContractDetail>) =>
    p
      .then((d) => {
        qc.setQueryData(["contract", c.id], d);
        void qc.invalidateQueries({ queryKey: ["contracts"] });
      })
      .catch((e) => toast.err(apiErrorMessage(e)));

  return (
    <section className="space-y-3">
      <h3 className="section-title">Key dates</h3>
      {c.notice_by && noticeIn != null && noticeIn >= 0 && c.status === "signed" && (
        <div
          className={clsx(
            "flex items-center gap-2 rounded-xl px-3 py-2 text-sm",
            noticeIn <= 30 ? "bg-accent-amber/10 text-accent-amber" : "bg-fg/[0.04] text-mut-300",
          )}
        >
          <BellRing className="h-4 w-4 shrink-0" />
          <span>
            Give notice by <span className="font-semibold">{fmtDay(c.notice_by)}</span> (
            {inDays(noticeIn)}) {c.auto_renew ? "or it renews" : "if you want out early"}
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Start"
          type="date"
          value={c.start_date}
          onSave={(v) => save.mutate({ start_date: v || null })}
        />
        <Field
          label="End"
          type="date"
          value={c.end_date}
          onSave={(v) => save.mutate({ end_date: v || null })}
        />
        <Field
          label="Notice (days)"
          type="number"
          value={c.notice_days}
          placeholder="e.g. 30"
          onSave={(v) => save.mutate({ notice_days: v ? Number(v) : null })}
        />
        <Field
          label="Signed on"
          type="date"
          value={c.signed_on}
          onSave={(v) => save.mutate({ signed_on: v || null })}
        />
      </div>
      <label className="flex items-center gap-2 text-md">
        <input
          type="checkbox"
          checked={c.auto_renew}
          onChange={(e) => save.mutate({ auto_renew: e.target.checked })}
        />
        Renews automatically
      </label>

      <div>
        <div className="label">Obligations &amp; milestones</div>
        <ul className="space-y-0.5">
          {c.milestones.map((m) => {
            const n = m.due_date ? daysUntil(m.due_date) : null;
            const late = !m.done_at && n != null && n < 0;
            return (
              <li key={m.id} className="group flex items-center gap-2 rounded-lg px-1 py-1">
                <input
                  type="checkbox"
                  aria-label={`Done: ${m.title}`}
                  checked={!!m.done_at}
                  onChange={(e) =>
                    void apply(Contracts.updateMilestone(c.id, m.id, { done: e.target.checked }))
                  }
                />
                <span
                  className={clsx(
                    "min-w-0 flex-1 truncate text-md",
                    m.done_at && "text-mut-500 line-through",
                  )}
                >
                  {m.title}
                </span>
                {m.due_date && (
                  <span
                    className={clsx(
                      "shrink-0 text-xs tabular-nums",
                      late ? "font-medium text-accent-pink" : "text-mut-500",
                    )}
                    title={fmtDay(m.due_date)}
                  >
                    {m.done_at ? fmtDayShort(m.due_date) : inDays(n!)}
                  </span>
                )}
                <button
                  className="text-mut-500 opacity-0 hover:text-accent-pink focus:opacity-100 group-hover:opacity-100"
                  aria-label={`Remove ${m.title}`}
                  onClick={() =>
                    void Contracts.removeMilestone(c.id, m.id)
                      .then(() => qc.invalidateQueries({ queryKey: ["contract", c.id] }))
                      .catch((e) => toast.err(apiErrorMessage(e)))
                  }
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
        <form
          className="mt-1.5 flex gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!title.trim()) return;
            void apply(Contracts.addMilestone(c.id, title.trim(), due || null)).then(() => {
              setTitle("");
              setDue("");
            });
          }}
        >
          <input
            className="input min-w-0 flex-1 !py-1.5"
            placeholder="e.g. Kick-off workshop"
            aria-label="New obligation"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <input
            className="input !w-[9.5rem] !py-1.5"
            type="date"
            aria-label="Due date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
          <button className="btn !px-2.5" aria-label="Add" disabled={!title.trim()}>
            <Plus className="h-4 w-4" />
          </button>
        </form>
      </div>
    </section>
  );
}

/** What the contract is, who it's with, and the key terms in plain words. */
export function TermsPanel({ contract: c }: { contract: ContractDetail }) {
  const save = useContractSave(c.id);
  const ownersQuery = useQuery({ queryKey: ["owners"], queryFn: () => Config.listOwners() });
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => Accounts.list() });
  const dealsQuery = useQuery({ queryKey: ["deals"], queryFn: () => Deals.list() });
  const deals = (dealsQuery.data ?? []).filter(
    (d) => !c.account_id || d.account_id === c.account_id || d.id === c.deal_id,
  );
  return (
    <section className="space-y-3">
      <h3 className="section-title">Terms</h3>
      <p className="text-xs text-mut-500">Changes save as you go and show in the thread.</p>
      <Field label="Title" value={c.title} onSave={(v) => v.trim() && save.mutate({ title: v })} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="terms-kind">
            Type
          </label>
          <select
            id="terms-kind"
            className="input"
            value={c.kind}
            onChange={(e) => save.mutate({ kind: e.target.value as ContractKind })}
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </div>
        <Field
          label="Value (€)"
          type="number"
          value={c.value_eur}
          onSave={(v) => save.mutate({ value_eur: v ? Number(v) : null })}
        />
        <div>
          <label className="label" htmlFor="terms-owner">
            Owner
          </label>
          <select
            id="terms-owner"
            className="input"
            value={c.owner_user_id ?? ""}
            onChange={(e) => save.mutate({ owner_user_id: e.target.value || null })}
          >
            <option value="">Nobody</option>
            {(ownersQuery.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <Field
          label="Their signer"
          value={c.counterparty_signer}
          placeholder="Name, title"
          onSave={(v) => save.mutate({ counterparty_signer: v.trim() || null })}
        />
        <div>
          <label className="label" htmlFor="terms-account">
            Account
          </label>
          <select
            id="terms-account"
            className="input"
            value={c.account_id ?? ""}
            onChange={(e) => save.mutate({ account_id: e.target.value || null })}
          >
            <option value="">No account</option>
            {(accountsQuery.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="terms-deal">
            Deal
          </label>
          <select
            id="terms-deal"
            className="input"
            value={c.deal_id ?? ""}
            onChange={(e) => save.mutate({ deal_id: e.target.value || null })}
          >
            <option value="">No deal</option>
            {deals.map((d) => (
              <option key={d.id} value={d.id}>
                {d.lead}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="label" htmlFor="terms-summary">
          Key terms
        </label>
        <textarea
          key={c.summary ?? ""}
          id="terms-summary"
          className="input min-h-[110px]"
          placeholder="Scope, payment terms, liability cap, IP, SLAs… in plain words."
          defaultValue={c.summary ?? ""}
          onBlur={(e) => {
            const v = e.target.value.trim() || null;
            if (v !== (c.summary ?? null)) save.mutate({ summary: v });
          }}
        />
      </div>
    </section>
  );
}
